var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useCallback, useRef } from "react";
import { createGraphClient } from "../graph/graphClient";
import { readCachedProfile, writeCachedProfile } from "../graph/profileCache";
import { fetchUserProfile } from "../graph/userProfile";
import { fetchProfilePhotoAsDataUri } from "../graph/profilePhoto";
/**
 * Hook that fetches the user's Graph profile and photo in parallel.
 * Re-fetches when accessToken changes (e.g., after silent token refresh).
 */
export function useGraphProfile(accessToken) {
    const [graphUser, setGraphUser] = useState(null);
    const [photoDataUri, setPhotoDataUri] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fetchTick, setFetchTick] = useState(0);
    const forceRefreshRef = useRef(false);
    const refresh = useCallback(() => {
        forceRefreshRef.current = true;
        setFetchTick((t) => t + 1);
    }, []);
    useEffect(() => {
        if (!accessToken)
            return;
        const forceRefresh = forceRefreshRef.current;
        forceRefreshRef.current = false;
        let cancelled = false;
        setError(null);
        if (!forceRefresh) {
            const cached = readCachedProfile();
            if (cached) {
                setGraphUser(cached.graphUser);
                setPhotoDataUri(cached.photoDataUri);
                setLoading(false);
                return;
            }
        }
        setLoading(true);
        const client = createGraphClient(accessToken);
        function load() {
            return __awaiter(this, void 0, void 0, function* () {
                var _a;
                try {
                    // Fetch profile and photo concurrently
                    const [user] = yield Promise.all([fetchUserProfile(client)]);
                    if (cancelled)
                        return;
                    setGraphUser(user);
                    // Fetch photo separately (may fail with 404 — handled in profilePhoto.ts)
                    const photo = yield fetchProfilePhotoAsDataUri(client, (_a = user.displayName) !== null && _a !== void 0 ? _a : "?");
                    if (!cancelled) {
                        setPhotoDataUri(photo);
                        writeCachedProfile(user, photo);
                    }
                }
                catch (err) {
                    if (!cancelled) {
                        const msg = err instanceof Error ? err.message : "Failed to load profile";
                        setError(msg);
                        console.error("[useGraphProfile]", err);
                    }
                }
                finally {
                    if (!cancelled)
                        setLoading(false);
                }
            });
        }
        void load();
        return () => {
            cancelled = true;
        };
    }, [accessToken, fetchTick]);
    return { graphUser, photoDataUri, loading, error, refresh };
}
